import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalCombobox } from '../../components/FormField/PortalCombobox';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { useToast } from '../../components/Toast/ToastContext';
import { lbsToKg, kgToLbs } from '../../lib/utils';
import { createPetMutation, updatePetMutation, rescuePetsQuery, allBreedsQuery } from '../../lib/rescueQueries';
import type { RescuePetsData, AllBreedsData } from '../../lib/rescueQueries';
import { trackPetCreated, trackPetEdited } from '../../analytics';
import styles from './PetFormModal.module.scss';

export interface PetFormPet {
  id: string;
  name: string;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  additionalInfo?: string | null;
  breed?: { name: string } | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
}

type PetFormModalProps =
  | {
      open: boolean;
      onClose: () => void;
      mode: 'create';
      /**
       * Called after the pet is successfully created. When provided, the parent
       * owns post-creation messaging and modal close — PetFormModal will skip
       * its default success toast and `onClose()` so the parent can chain
       * follow-up work (e.g. assigning a device to the new pet).
       */
      onCreated?: (pet: { id: string; name: string }) => void | Promise<void>;
    }
  | { open: boolean; onClose: () => void; mode: 'edit'; pet: PetFormPet | null };

function parseSpayNeuter(additionalInfo?: string | null): string {
  if (!additionalInfo) {
    return 'Unknown';
  }
  const lower = additionalInfo.toLowerCase();
  if (lower.includes('spayed')) {
    return 'Spayed';
  }
  if (lower.includes('neutered')) {
    return 'Neutered';
  }
  if (lower.includes('intact')) {
    return 'Intact';
  }
  return 'Unknown';
}

export default function PetFormModal(props: PetFormModalProps) {
  const { open, onClose, mode } = props;
  const pet = mode === 'edit' ? props.pet : null;
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [species, setSpecies] = useState('DOG');
  const [gender, setGender] = useState('FEMALE');
  const [weight, setWeight] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [breedId, setBreedId] = useState('');
  const [color, setColor] = useState('');
  const [spayNeuter, setSpayNeuter] = useState('Unknown');

  // Only needed in create mode to derive the rescue household id.
  //
  // errorPolicy 'all' keeps partial data when the response carries unrelated
  // GraphQL errors — e.g. a pet's chip contact-history entry with a null value
  // in a non-nullable field. With the default policy Apollo discards the entire
  // result on any error, leaving `householdId` null and permanently disabling
  // the Create Pet button even though the rescue household resolved fine.
  const { data: petsData } = useQuery<RescuePetsData>(rescuePetsQuery, {
    skip: mode !== 'create',
    errorPolicy: 'all',
  });
  const { data: breedsData } = useQuery<AllBreedsData>(allBreedsQuery, {
    variables: { species },
    skip: !open || (mode === 'edit' && !pet),
  });

  const householdId = useMemo(() => {
    // Optional-chained: a partial (errored) response may null out branches.
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    return rescueHousehold?.household.id ?? null;
  }, [petsData]);

  const breedOptions = useMemo(() => {
    const list = breedsData?.allBreeds ?? [];
    const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.map((b) => ({ value: b.id, label: b.name }));
  }, [breedsData]);

  const otherBreedId = useMemo(() => {
    return breedOptions.find((b) => b.label === 'Other')?.value ?? '';
  }, [breedOptions]);

  // Reset (create) or pre-fill (edit) the form whenever the modal opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'create') {
      setName('');
      setSpecies('DOG');
      setGender('FEMALE');
      setWeight('');
      setBirthYear('');
      setBirthMonth('');
      setBirthDay('');
      setBreedId('');
      setColor('');
      setSpayNeuter('Unknown');
      return;
    }
    if (!pet) {
      return;
    }
    setName(pet.name ?? '');
    setSpecies(pet.species ?? 'DOG');
    setGender(pet.gender ?? 'FEMALE');
    const lbs = kgToLbs(pet.weight);
    setWeight(lbs ? String(lbs) : '');
    setBirthYear(pet.yearOfBirth ? String(pet.yearOfBirth) : '');
    setBirthMonth(pet.monthOfBirth ? String(pet.monthOfBirth) : '');
    setBirthDay(pet.dayOfBirth ? String(pet.dayOfBirth) : '');
    setColor(pet.color ?? '');
    setSpayNeuter(parseSpayNeuter(pet.additionalInfo));
    setBreedId('');
  }, [open, mode, pet]);

  // In edit mode, resolve the pet's breed name → breed id once breeds load.
  useEffect(() => {
    if (mode !== 'edit' || !pet?.breed?.name || breedOptions.length === 0) {
      return;
    }
    const match = breedOptions.find((b) => b.label === pet.breed?.name);
    if (match) {
      setBreedId(match.value);
    }
  }, [mode, pet, breedOptions]);

  const [createPet, { loading: creating }] = useMutation<{ createPet: { id: string; name: string } }>(
    createPetMutation,
    {
      refetchQueries: 'active' as const,
    },
  );
  const [updatePet, { loading: saving }] = useMutation(updatePetMutation, {
    refetchQueries: 'active' as const,
  });
  const submitting = creating || saving;

  async function handleSubmit() {
    if (!name.trim() || !birthYear || !birthMonth) {
      return;
    }
    const additionalParts: string[] = [];
    if (spayNeuter && spayNeuter !== 'Unknown') {
      additionalParts.push(spayNeuter);
    }
    // In edit mode, send null for blank optional fields so the user can
    // clear them. In create mode, send undefined so the field is omitted.
    const blank = mode === 'edit' ? null : undefined;

    // Birth date — schema vs. resolver:
    //   - GraphQL schema (CreatePetInput) declares `yearOfBirth: Int!` and
    //     `monthOfBirth: Int` (year required, month nullable). UpdatePetInput
    //     declares both as nullable.
    //   - The resolver (`validateBirthDateInputs` in
    //     web/src/lib/pet/services/mutators.ts) requires year AND month
    //     together when any birth field is provided. That validator is
    //     gated behind the `pet.enableBirthDateValidation` feature flag,
    //     which is currently ON in production — so the runtime contract is
    //     stricter than the schema suggests.
    // We require year + month in the form to match the active resolver
    // contract; day stays optional.
    const baseInput = {
      name: name.trim(),
      species,
      gender,
      weight: lbsToKg(weight ? parseFloat(weight) : 0),
      yearOfBirth: birthYear ? parseInt(birthYear, 10) : blank,
      monthOfBirth: birthMonth ? parseInt(birthMonth, 10) : blank,
      dayOfBirth: birthDay ? parseInt(birthDay, 10) : blank,
      color: color.trim() || blank,
      additionalInfo: additionalParts.join(', ') || blank,
    };

    try {
      if (mode === 'create') {
        if (!householdId) {
          showToast('Unable to find your rescue household. Please try again later.');
          return;
        }
        const result = await createPet({
          variables: {
            input: {
              ...baseInput,
              breedId: breedId || otherBreedId || undefined,
              isPurebred: false,
              householdId,
            },
          },
        });
        trackPetCreated(species);
        // When the parent supplied `onCreated`, hand off post-creation work
        // (toast, close, follow-up mutations) to them. We swallow rejections
        // so PetFormModal doesn't surface a misleading "Failed to create pet"
        // toast for errors that happened *after* the pet was created.
        if (props.mode === 'create' && props.onCreated) {
          const created = result.data?.createPet;
          if (created) {
            try {
              await props.onCreated({ id: created.id, name: created.name });
            } catch {
              // Parent owns error display.
            }
          }
          return;
        }
        showToast(`${name.trim()} created successfully`);
      } else {
        if (!pet) {
          return;
        }
        await updatePet({
          variables: {
            input: {
              ...baseInput,
              id: pet.id,
              breedId: breedId || null,
            },
          },
        });
        trackPetEdited(species);
        showToast(`${name.trim()} updated`);
      }
      onClose();
    } catch (err) {
      const verb = mode === 'create' ? 'create' : 'update';
      showToast(`Failed to ${verb} pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  const canSubmit =
    name.trim().length > 0 &&
    birthYear.length > 0 &&
    birthMonth.length > 0 &&
    (mode !== 'create' || !!householdId) &&
    !submitting;

  if (mode === 'edit' && !pet) {
    return null;
  }

  const title = mode === 'create' ? 'Add Pet' : 'Edit Pet Profile';
  const subtitle =
    mode === 'create'
      ? 'Create a pet profile for your rescue. You can add a microchip or device later.'
      : `Update ${pet?.name ?? 'this pet'}’s profile details.`;
  const submitLabel =
    mode === 'create' ? (creating ? 'Creating...' : 'Create Pet') : saving ? 'Saving...' : 'Save Changes';

  return (
    <Modal open={open} onClose={onClose} size="wide">
      <ModalTitle>{title}</ModalTitle>
      <p className={styles.subtitle}>{subtitle}</p>

      {/* Name + Species */}
      <div className={styles.row2}>
        <PortalField label="Pet Name *">
          <PortalInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Biscuit" autoFocus />
        </PortalField>
        <PortalField label="Species *">
          <ToggleGroup
            options={[
              { value: 'DOG', label: 'Dog' },
              { value: 'CAT', label: 'Cat' },
            ]}
            value={species}
            onChange={(val) => {
              setSpecies(val);
              setBreedId('');
            }}
            style={{ marginTop: 2 }}
          />
        </PortalField>
      </div>

      {/* Breed + Gender */}
      <div className={styles.row2}>
        <PortalField label="Breed">
          <PortalCombobox
            options={breedOptions}
            value={mode === 'create' ? breedId || otherBreedId : breedId}
            onChange={setBreedId}
            placeholder="e.g. Lab Mix"
          />
        </PortalField>
        <PortalField label="Gender">
          <PortalSelect
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              if (e.target.value === 'MALE' && spayNeuter === 'Spayed') {
                setSpayNeuter('Unknown');
              }
              if (e.target.value === 'FEMALE' && spayNeuter === 'Neutered') {
                setSpayNeuter('Unknown');
              }
            }}
          >
            <option value="FEMALE">Female</option>
            <option value="MALE">Male</option>
          </PortalSelect>
        </PortalField>
      </div>

      {/* Birth Year / Month / Day */}
      <div className={styles.row3}>
        <PortalField label="Birth Year *">
          <PortalInput
            type="number"
            min="2000"
            max={new Date().getFullYear()}
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            placeholder="e.g. 2023"
          />
        </PortalField>
        <PortalField label="Month *">
          <PortalInput
            type="number"
            min="1"
            max="12"
            value={birthMonth}
            onChange={(e) => setBirthMonth(e.target.value)}
            placeholder="e.g. 3"
          />
        </PortalField>
        <PortalField label="Day" className={styles.optionalField}>
          <PortalInput
            type="number"
            min="1"
            max="31"
            value={birthDay}
            onChange={(e) => setBirthDay(e.target.value)}
            placeholder="Optional"
          />
        </PortalField>
      </div>

      {mode === 'edit' && (
        <PortalField label="Spay/Neuter Status *">
          <PortalSelect value={spayNeuter} onChange={(e) => setSpayNeuter(e.target.value)}>
            {gender === 'FEMALE' && <option value="Spayed">Spayed</option>}
            {gender === 'MALE' && <option value="Neutered">Neutered</option>}
            <option value="Intact">Intact</option>
            <option value="Unknown">Unknown</option>
          </PortalSelect>
        </PortalField>
      )}
      <PortalField label="Color">
        <PortalInput value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Black & White" />
      </PortalField>

      {/* Weight */}
      <PortalField label="Weight (lbs)">
        <PortalInput
          type="number"
          min="0"
          step="0.1"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          placeholder="Optional"
        />
      </PortalField>

      {mode === 'create' && (
        <div className={styles.infoBanner}>
          Creating a pet profile first means you won&rsquo;t need to re-enter this data later.
        </div>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </ModalActions>
    </Modal>
  );
}
